import type { Ref } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Schedule a Tour',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/schedule-a-tour',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: {
      label: 'Tracked Event',
      type: 'event',
      defaultValue: 'schedule_tour_clicked',
    },
  },
};

export function ReviewsCallout() {
  const ctaLink = useSlotGroup('reviewscallout.cta_link', ctaLinkDef);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="bg-primary py-10 md:py-14">
      <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
        <div ref={sectionRef as Ref<HTMLDivElement>} className="reveal-fade-up">
          <RichTextContent
            path="reviewscallout.title"
            as="h2"
            className="font-heading text-3xl font-normal leading-snug text-primary-foreground sm:text-4xl [&_p]:m-0"
          />
          <RichTextContent
            path="reviewscallout.subtitle"
            as="p"
            className="mx-auto mt-2 max-w-2xl font-body text-lg text-primary-foreground [&_p]:m-0"
          />
          <div className="mt-6">
            <EditableSlotGroup groupPath="reviewscallout.cta_link" definition={ctaLinkDef}>
              <a
                href={ctaLink.url || '/schedule-a-tour'}
                target={ctaLink._target || '_self'}
                data-vibesite-event={ctaLink.vibesite_event || 'schedule_tour_clicked'}
                className="tour-cta group/cta relative inline-flex items-center gap-2.5 overflow-hidden px-10 py-4 text-sm font-semibold uppercase tracking-[0.2em] text-primary-foreground transition-all duration-300"
              >
                <span className="relative z-10">{ctaLink.label || 'Schedule a Tour'}</span>
                <svg
                  className="relative z-10 h-4 w-4 transition-transform duration-300 group-hover/cta:translate-x-0.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                </svg>
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        .tour-cta {
          background: linear-gradient(135deg, var(--vibe-color-primary) 0%, color-mix(in srgb, var(--vibe-color-primary) 80%, black) 100%);
          border: 1px solid #000;
          border-radius: 2px;
          box-shadow:
            0 2px 6px rgba(0, 0, 0, 0.2),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }
        .tour-cta::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.15) 50%, transparent 100%);
          transition: left 0.5s ease;
        }
        .tour-cta:hover {
          background: linear-gradient(135deg, color-mix(in srgb, var(--vibe-color-primary) 90%, white) 0%, var(--vibe-color-primary) 100%);
          box-shadow:
            0 4px 12px rgba(0, 0, 0, 0.2),
            0 8px 24px color-mix(in srgb, var(--vibe-color-primary) 40%, transparent);
          transform: translateY(-2px);
        }
        .tour-cta:hover::before {
          left: 100%;
        }
        .tour-cta:active {
          transform: translateY(0);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ReviewsCallout',
  displayName: 'Schedule a Tour CTA',
  tokens: {
    'reviewscallout.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Schedule a Tour</p>',
    },
    'reviewscallout.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>See <span data-type="field-token" data-field="property_name"></span> in person and find the home that fits.</p>',
    },
  },
  slotGroups: {
    'reviewscallout.cta_link': ctaLinkDef,
  },
};

(ReviewsCallout as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ReviewsCallout;
